import React, { useState, useEffect, useCallback, useRef } from "react";
import { Tooltip, CircularProgress } from "@mui/material";
import {
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  DocumentChartBarIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";
import AxiosInstance from "../../../utils/axiosInstance";
import NoRows from "../../../components/DataGrid/animations/NoRows";

// ── TYPES ──────────────────────────────────────────────────────────────────────

interface PaginationStats {
  totalRecords: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

interface PayrollExportFile {
  id: number;
  fileName: string;
  createdOn: string;
  createdBy: string;
  filePath?: string;
}

interface PayrollExportDetail {
  id?: number;
  jobNumber?: string;
  employeeNumber?: string;
  employeeName?: string;
  bonusType?: string;
  bonusTypeId?: number;
  glCode?: string;
  earningCode?: string;
  earningDescription?: string;
  debitAmount?: number;
  creditAmount?: number;
  paidDate?: string;
  referenceNumber?: string;
  company?: string;
  subJob?: string;
  isCorrection?: boolean;
  isPaymentReversal?: boolean;
}

interface PayrollExportJob {
  jobId?: number;
  jobNumber?: string;
  bonusTypeId?: number;
  bonusType?: string;
  totalDebit?: number;
  totalCredit?: number;
  payDate?: string;
  hasNegativeBonus?: boolean;
  builderCount?: number;
}

interface PayrollExportBuilder {
  employeeNumber?: string;
  employeeName?: string;
  jobId?: number;
  jobNumber?: string;
  bonusTypeId?: number;
  bonusType?: string;
  totalDebit?: number;
  totalCredit?: number;
  payDate?: string;
  hasNegativeBonus?: boolean;
}

interface ExportFilesResponse {
  files: PayrollExportFile[];
  paginationStats: PaginationStats;
}

interface ExportDetailsResponse {
  details: PayrollExportDetail[];
  paginationStats: PaginationStats;
}

interface ExportJobsResponse {
  jobs: PayrollExportJob[];
  paginationStats: PaginationStats;
}

interface ExportBuildersResponse {
  builders: PayrollExportBuilder[];
  paginationStats: PaginationStats;
}

type DetailTab = "details" | "jobs" | "builders";

// ── HELPERS ────────────────────────────────────────────────────────────────────

const fmt = (n?: number) =>
  (n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2 });

const PAGE_SIZE = 10;

/** Infer bonus type from filename for mobile badge */
function inferBonusType(fileName: string): "Closing" | "Stage5" | null {
  const l = fileName.toLowerCase();
  if (l.includes("closing")) return "Closing";
  if (l.includes("stage5") || l.includes("stg5") || l.includes("stage 5")) return "Stage5";
  return null;
}

const bonusTypeCls = (type?: string) =>
  type === "Closing" ? "bg-purple-100 text-purple-700" : "bg-emerald-100 text-emerald-700";

// ── PAGINATION ─────────────────────────────────────────────────────────────────

function Pagination({
  stats,
  onPageChange,
}: {
  stats: PaginationStats;
  onPageChange: (page: number) => void;
}) {
  if (!stats || stats.totalPages <= 1) return null;
  const { currentPage, totalPages, totalRecords, pageSize } = stats;
  const from = (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalRecords);

  return (
    <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
      <span className="text-xs text-gray-500">
        Showing {from}–{to} of {totalRecords} records
      </span>
      <div className="flex items-center gap-1">
        <button
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          className="p-1.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeftIcon className="w-3.5 h-3.5" />
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let p: number;
          if (totalPages <= 5) p = i + 1;
          else if (currentPage <= 3) p = i + 1;
          else if (currentPage >= totalPages - 2) p = totalPages - 4 + i;
          else p = currentPage - 2 + i;
          return (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`w-7 h-7 rounded border text-xs font-medium transition-colors ${
                p === currentPage
                  ? "bg-primary-600 text-white border-primary-600"
                  : "border-gray-300 text-gray-600 hover:bg-gray-100"
              }`}
            >
              {p}
            </button>
          );
        })}
        <button
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          className="p-1.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronRightIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── FILTER CARD ────────────────────────────────────────────────────────────────

function FilterCard({
  search, dateFrom, dateTo, hasFilters, loading, pagination,
  onSearchChange, onSearchClear, onDateFromChange, onDateToChange, onClear,
}: {
  search: string;
  dateFrom: string;
  dateTo: string;
  hasFilters: boolean;
  loading: boolean;
  pagination: PaginationStats | null;
  onSearchChange: (v: string) => void;
  onSearchClear: () => void;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
  onClear: () => void;
}) {
  // Collapsed by default
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-4 overflow-hidden">
      {/* Toggle header */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Filters</span>
          {hasFilters && (
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary-600 text-white text-[9px] font-bold">
              {[search, dateFrom, dateTo].filter(Boolean).length}
            </span>
          )}
        </div>
        <ChevronRightIcon
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? "rotate-90" : ""}`}
        />
      </button>

      {/* Collapsible body */}
      {open && (
        <>
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-5 gap-y-4 pt-4">

              {/* File Name Search */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-600">File Name</label>
                <div className="flex items-center gap-2 px-3 py-[9px] rounded-lg border border-gray-300 bg-white focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100 transition-all">
                  <MagnifyingGlassIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Search file name…"
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="bg-transparent outline-none flex-1 min-w-0 text-sm text-gray-800 placeholder-gray-400"
                  />
                  {search && (
                    <button onClick={onSearchClear} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Date From */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-600">Date From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => onDateFromChange(e.target.value)}
                  className="w-full px-3 py-[9px] rounded-lg border border-gray-300 text-sm text-gray-800 bg-white outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>

              {/* Date To */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-600">Date To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => onDateToChange(e.target.value)}
                  className="w-full px-3 py-[9px] rounded-lg border border-gray-300 text-sm text-gray-800 bg-white outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>
            </div>
          </div>

          {/* Footer bar */}
          <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border-t border-gray-200">
            <button
              onClick={onClear}
              disabled={!hasFilters}
              className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Clear Filters
            </button>
            <span className="text-xs text-gray-500">
              {loading
                ? "Loading…"
                : pagination
                  ? `Showing ${pagination.totalRecords} file${pagination.totalRecords !== 1 ? "s" : ""}`
                  : "No results"}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

// ── MAIN COMPONENT ─────────────────────────────────────────────────────────────

const PayrollExports: React.FC = () => {
  const [files, setFiles] = useState<PayrollExportFile[]>([]);
  const [pagination, setPagination] = useState<PaginationStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const handleSearchChange = (val: string) => {
    setSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebouncedSearch(val); setPage(1); }, 350);
  };

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, pageSize: PAGE_SIZE };
      if (debouncedSearch) params.fileName      = debouncedSearch;
      if (dateFrom)        params.createdOnFrom = dateFrom;
      if (dateTo)          params.createdOnTo   = dateTo;
      const res = await AxiosInstance.get<ExportFilesResponse>("/api/payroll/export/files", { params });
      setFiles(res.data?.files ?? []);
      setPagination(res.data?.paginationStats ?? null);
    } catch (err) { console.error(err); setFiles([]); }
    finally { setLoading(false); }
  }, [page, debouncedSearch, dateFrom, dateTo]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleDownload = async (f: PayrollExportFile, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (downloadingId === f.id) return;
    setDownloadingId(f.id);
    try {
      const res = await AxiosInstance.get(`/api/payroll/export/files/${f.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = f.fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error("Download failed", err); }
    finally { setDownloadingId(null); }
  };

  const clearFilters = () => {
    setSearch(""); setDebouncedSearch(""); setDateFrom(""); setDateTo(""); setPage(1);
  };

  const hasFilters = !!(debouncedSearch || dateFrom || dateTo);

  return (
    <div className="min-h-full p-4">

      {/* ── Page header ── */}
      <div className="mb-5">
        <h1 className="text-lg font-semibold text-gray-900">Payroll Exports</h1>
        <p className="text-xs text-gray-500 mt-0.5">
          Excel files generated and sent to the payroll processor — click a file name to download the file for details
        </p>
      </div>

      {/* ── Filter card — collapsible, closed by default ── */}
      <FilterCard
        search={search}
        dateFrom={dateFrom}
        dateTo={dateTo}
        hasFilters={hasFilters}
        loading={loading}
        pagination={pagination}
        onSearchChange={handleSearchChange}
        onSearchClear={() => { setSearch(""); setDebouncedSearch(""); setPage(1); }}
        onDateFromChange={(v) => { setDateFrom(v); setPage(1); }}
        onDateToChange={(v) => { setDateTo(v); setPage(1); }}
        onClear={clearFilters}
      />

      {/* ── Table / Cards ── */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><CircularProgress /></div>
        ) : files.length === 0 ? (
          <div className="flex items-center justify-center py-20"><NoRows /></div>
        ) : (
          <>
            {/* ── Desktop table ── */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Generated By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Generated On
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {files.map((f) => {
                    const isDownloading = downloadingId === f.id;
                    return (
                      <tr key={f.id} className="hover:bg-gray-50">
                        {/* File Name — click to open detail panel */}
                        <td className="px-6 py-4">
                          <span className="font-medium text-xs text-gray-800 truncate max-w-[400px] block">
                            {f.fileName}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {f.createdBy?.split("@")[0] ?? "—"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {f.createdOn ? moment(f.createdOn).format("MM/DD/YYYY h:mm A") : "—"}
                        </td>
                        {/* Action */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <Tooltip title="Download file">
                              <button
                                onClick={(e) => handleDownload(f, e)}
                                disabled={isDownloading}
                                className="p-1.5 rounded-md border border-gray-300 text-gray-500 hover:text-primary-600 hover:border-primary-300 hover:bg-primary-50 transition-colors disabled:opacity-50"
                              >
                                {isDownloading
                                  ? <CircularProgress size={14} />
                                  : <ArrowDownTrayIcon className="w-4 h-4" />
                                }
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Mobile cards ── */}
            <div className="md:hidden">
              {files.map((f) => {
                const isDownloading = downloadingId === f.id;
                const bonusType = inferBonusType(f.fileName);
                const generatedBy = f.createdBy?.split("@")[0] ?? "Unknown";
                const initial = generatedBy[0]?.toUpperCase() ?? "?";

                return (
                  <div
                    key={f.id}
                    className="relative flex gap-4 px-4 py-4 border-b border-gray-100 last:border-b-0 transition-colors"
                  >
                    {/* Left icon */}
                    <div className="flex-shrink-0 flex items-start pt-0.5">
                      <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                        <DocumentChartBarIcon className="w-5 h-5 text-indigo-500" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Filename */}
                      <p className="text-[12px] font-semibold text-gray-900 leading-snug truncate mb-1.5">
                        {f.fileName}
                      </p>

                      {/* Tags */}
                      <div className="flex items-center flex-wrap gap-1.5 mb-2">
                        {bonusType && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${bonusType === "Closing" ? "bg-purple-100 text-purple-700" : "bg-emerald-100 text-emerald-700"}`}>
                            {bonusType === "Stage5" ? "Stage 5" : "Closing"}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-500">
                          <CalendarDaysIcon className="w-2.5 h-2.5" />
                          {f.createdOn ? moment(f.createdOn).format("MMM D, YYYY") : "—"}
                        </span>
                      </div>

                      {/* Bottom row */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="w-5 h-5 rounded-full bg-gray-200 text-gray-600 text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                            {initial}
                          </span>
                          <span className="text-[11px] text-gray-500 truncate">{generatedBy}</span>
                        </div>
                        {/* Download — stops card tap from opening detail panel */}
                        <div
                          className="flex-shrink-0"
                          onClick={(e) => { e.stopPropagation(); handleDownload(f, e); }}
                        >
                          <span className={`flex items-center gap-1 text-[10.5px] font-medium px-2 py-1 rounded-md border transition-colors ${
                            isDownloading
                              ? "border-primary-200 bg-primary-50 text-primary-500"
                              : "border-gray-200 text-gray-400 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50"
                          }`}>
                            {isDownloading
                              ? <><CircularProgress size={10} /><span>Downloading…</span></>
                              : <><ArrowDownTrayIcon className="w-3 h-3" /><span>Download</span></>
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {pagination && <Pagination stats={pagination} onPageChange={setPage} />}
      </div>

    </div>
  );
};

export default PayrollExports;